import { useEffect, useState } from 'react'
import { PageSkeleton } from '@/components/templates/page-skeleton'
import { hasExpiredDemoSignIn } from '@/lib/demoSignInExpiry'
import { MemberPortalOrganizationDisclosure } from '@/components/member-portal/MemberPortalOrganizationDisclosure'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { LogOut } from 'lucide-react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { MemberSignIn, type MemberSessionResponse } from '@/components/member-portal/MemberSignIn'
import {
  memberSessionKey,
  replaceMemberSession,
} from '@/components/member-portal/memberPortalSession'
import {
  MemberPortalContent,
  memberPortalDestinationPath,
  memberPortalDetailPath,
} from '@/components/member-portal/MemberPortalContent'
import {
  directoryEnabled,
  type DirectoryResponse,
} from '@/components/member-portal/MemberPortalDirectory'
import type { Association } from '@/pages/StaffSignInPage'
import { Button } from '@/components/ui/button'
import { deleteJSON, getJSON } from '@/lib/api'
import { sessionQueryOptions } from '@/lib/sessionQueryOptions'
import { resumeMemberSignIn } from '@/lib/memberSignInContinuation'
import type { MemberStatusSource } from '@/components/member-portal/memberPortalPresentation'

const portalDestinations = [
  { label: 'Overview', path: '/portal' },
  { label: 'Profile', path: '/portal/profile' },
  { label: 'Billing', path: '/portal/billing' },
  { label: 'Membership', path: '/portal/membership' },
  { label: 'Events', path: '/portal/events' },
  { label: 'My learning', path: '/portal/my-learning' },
  { label: 'Email preferences', path: '/portal/email-preferences' },
] as const

export default function MemberPortalPage({ association }: { association: Association }) {
  const [expiredDemoLink, setExpiredDemoLink] = useState(hasExpiredDemoSignIn)
  const resumeToken = new URLSearchParams(window.location.search).get('member_sign_in')
  const session = useQuery<MemberSessionResponse>({
    queryKey: memberSessionKey,
    queryFn: () => getJSON('/api/v1/member_portal/session'),
    retry: false,
    ...sessionQueryOptions,
  })
  useEffect(() => {
    if (session.data && resumeToken && !expiredDemoLink) resumeMemberSignIn(resumeToken)
  }, [session.data, resumeToken, expiredDemoLink])

  if (expiredDemoLink)
    return (
      <MemberSignIn
        association={association}
        expiredDemoLink
        onSignedIn={() => setExpiredDemoLink(false)}
      />
    )
  if (session.isLoading) return <MemberSessionLoading association={association} />
  if (session.data && resumeToken) {
    return <MemberSessionLoading association={association} />
  }
  if (session.data)
    return <MemberPortalFrame association={association} person={session.data.person} />

  return <MemberSignIn association={association} />
}

function MemberSessionLoading({ association }: { association: Association }) {
  return <PageSkeleton layout="portal" label={`Checking your ${association.name} session...`} />
}

function MemberPortalFrame({
  association,
  person,
}: {
  association: Association
  person: MemberSessionResponse['person']
}) {
  const queryClient = useQueryClient()
  const location = useLocation()
  const navigate = useNavigate()
  const signOut = useMutation({
    mutationFn: () => deleteJSON('/api/v1/member_portal/session'),
    onSuccess: () => replaceMemberSession(queryClient, null),
  })
  const directory = useQuery<DirectoryResponse>({
    queryKey: ['member-portal-directory-availability'],
    queryFn: () => getJSON('/api/v1/member_directory?page=1'),
    enabled: memberAccess(person),
    retry: false,
  })
  const giving = useQuery<{ giving: { has_gifts: boolean } }>({
    queryKey: ['member-portal-giving-availability'],
    queryFn: () => getJSON('/api/v1/member_portal/giving'),
    retry: false,
  })
  const baseDestinations = giving.data?.giving?.has_gifts
    ? [
        ...portalDestinations.slice(0, 4),
        { label: 'Giving', path: '/portal/giving' as const },
        ...portalDestinations.slice(4),
      ]
    : portalDestinations
  const destinations = directoryEnabled(directory.data)
    ? [
        ...baseDestinations.slice(0, 1),
        { label: 'Directory', path: '/portal/directory' as const },
        ...baseDestinations.slice(1),
      ]
    : baseDestinations
  const leaderDestinations = portalLeaderDestination(person.leader_chapters)
  const organizationDestinations = managedOrganizationDestination(person.organizations)
  const allDestinations = [...destinations, ...leaderDestinations, ...organizationDestinations]
  const destinationPath = memberPortalDestinationPath(location.pathname)
  const widePortal = location.pathname.endsWith('/email')
  const activeDestination =
    allDestinations.find(({ path }) => path === location.pathname) ??
    allDestinations.find(({ path }) => path === destinationPath) ??
    allDestinations[0]
  const billing = useQuery<{
    billing: {
      currency: string
      open_invoices: Array<{
        id: number
        number: number
        origin: string
        status: string
        due_on: string
        balance: string
        balance_cents: number
      }>
    }
  }>({
    queryKey: ['member-portal-billing'],
    queryFn: () => getJSON('/api/v1/member_portal/billing'),
    enabled: destinationPath === '/portal' && Boolean(person.email),
    retry: false,
  })

  return (
    <div className="min-h-screen bg-background text-foreground">
      <MemberPortalHeader
        association={association}
        person={person}
        destinations={allDestinations}
        activeDestination={activeDestination}
        onSignOut={() => signOut.mutate()}
        signingOut={signOut.isPending}
      />
      <main className={`mx-auto w-full px-6 py-8 ${widePortal ? 'max-w-5xl' : 'max-w-3xl'}`}>
        {!memberPortalDetailPath(location.pathname) && (
          <h1 className="text-subheading font-semibold">{activeDestination.label}</h1>
        )}
        <MemberPortalOrganizationDisclosure
          path={location.pathname}
          organizations={person.organization_disclosures}
        />
        <MemberPortalContent
          path={location.pathname}
          associationName={association.name}
          membership={person.membership}
          memberAccess={memberAccess(person)}
          person={{ ...person, emailSuppressed: person.email_suppressed }}
          openInvoices={billing.data?.billing?.open_invoices}
          currency={billing.data?.billing?.currency}
          memberStatusSources={person.member_status_sources as MemberStatusSource[] | undefined}
          organizations={person.organizations}
          leaderChapters={person.leader_chapters}
          blastPaused={person.blast_paused}
          onOrganizationChange={(organizationId) =>
            navigate(`/portal/organization/${organizationId}`)
          }
        />
      </main>
    </div>
  )
}

function portalLeaderDestination(chapters: MemberSessionResponse['person']['leader_chapters']) {
  return chapters?.length ? [{ label: 'Chapter', path: '/portal/leader/chapters' }] : []
}

function memberAccess(person: MemberSessionResponse['person']) {
  return person.member_access?.member ?? Boolean(person.membership)
}

function managedOrganizationDestination(
  organizations?: MemberSessionResponse['person']['organizations'],
) {
  const firstOrganization = organizations?.[0]
  return firstOrganization ? [{ label: 'Organization', path: '/portal/organization' }] : []
}

function MemberPortalHeader({
  association,
  person,
  destinations,
  activeDestination,
  onSignOut,
  signingOut,
}: {
  association: Association
  person: MemberSessionResponse['person']
  destinations: ReadonlyArray<{ label: string; path: string }>
  activeDestination: { label: string; path: string }
  onSignOut: () => void
  signingOut: boolean
}) {
  return (
    <header className="border-b border-border bg-card">
      <div className="mx-auto flex h-16 max-w-screen-2xl items-center justify-between gap-4 px-6">
        <div className="flex min-w-0 items-center gap-3">
          <span
            aria-label={`${association.name} mark`}
            className="flex size-8 shrink-0 items-center justify-center rounded-md bg-primary text-body-sm font-semibold text-primary-foreground"
          >
            {association.name.slice(0, 1)}
          </span>
          <span className="truncate text-body-sm font-medium">{association.name}</span>
        </div>
        <div className="flex shrink-0 items-center gap-2 sm:gap-4">
          <span className="hidden text-body-sm text-muted-foreground sm:inline">{person.name}</span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="px-2 text-muted-foreground shadow-none"
            onClick={onSignOut}
            disabled={signingOut}
          >
            <LogOut className="mr-2 size-4" aria-hidden="true" />
            {signingOut ? 'Signing out...' : 'Sign out'}
          </Button>
        </div>
      </div>
      <nav className="mx-auto max-w-screen-2xl overflow-x-auto px-6" aria-label="Member portal">
        <div className="flex min-w-max gap-6">
          {destinations.map((destination) => {
            const isActive = destination.path === activeDestination.path
            return (
              <Link
                key={destination.path}
                to={destination.path}
                aria-current={isActive ? 'page' : undefined}
                className={`relative py-3 text-body-sm ${isActive ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
              >
                {destination.label}
                {isActive && (
                  <span
                    className="absolute inset-x-0 bottom-0 h-0.5 bg-primary"
                    aria-hidden="true"
                  />
                )}
              </Link>
            )
          })}
        </div>
      </nav>
    </header>
  )
}
